import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageHeader, KpiTile, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { dt, money, pct, title } from "@/lib/format";
import { ImportWizard } from "@/components/lists/ImportWizard";
import { useAuth } from "@/context/AuthContext";

const Distribution = ({ label, rows, testId }) => (
  <div className="rounded-lg border border-slate-200 p-3" data-testid={testId}>
    <p className="kpi-label mb-2">{label}</p>
    <div className="space-y-1">
      {(rows || []).length ? rows.map((row) => (
        <div key={row.value} className="flex items-center justify-between text-xs">
          <span className="text-slate-600">{title(String(row.value))}</span>
          <span className="font-medium text-slate-800">{row.count.toLocaleString()}</span>
        </div>
      )) : <p className="text-xs text-slate-400">No data yet</p>}
    </div>
  </div>
);

export default function ContactListDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const [importOpen, setImportOpen] = useState(false);
  const [cursor, setCursor] = useState(null);

  const list = useQuery({ queryKey: ["list", id], queryFn: () => get(`/contact-lists/${id}`), refetchInterval: 10000 });
  const summary = useQuery({ queryKey: ["list-summary", id], queryFn: () => get(`/contact-lists/${id}/summary`), refetchInterval: 15000 });
  const classification = useQuery({ queryKey: ["list-class", id], queryFn: () => get(`/contact-lists/${id}/classification`) });
  const contacts = useQuery({
    queryKey: ["list-contacts", id, cursor],
    queryFn: () => get(`/contact-lists/${id}/contacts?limit=50${cursor ? `&cursor=${cursor}` : ""}`),
  });
  const errors = useQuery({ queryKey: ["list-errors", id], queryFn: () => get(`/contact-lists/${id}/import-errors?page=1&page_size=50`) });

  const s = summary.data || {};
  const data = list.data || {};

  return (
    <>
      <PageHeader
        testId="list-detail-page"
        title={data.name || "Contact list"}
        subtitle={`${data.original_filename || "no source file"} · imported by ${data.created_by_name || "—"} · ${dt(data.created_at)}`}
        actions={
          <div className="flex flex-wrap gap-2">
            <StatusBadge value={data.status === "completed" ? "completed" : "running"} label={title(data.status || "draft")} testId="list-detail-status" />
            {can("lists", "write") ? (
              <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="detail-import-button">Import CSV</Button>
            ) : null}
            <Button variant="outline" size="sm" asChild data-testid="detail-back">
              <Link to="/lists">Back to lists</Link>
            </Button>
          </div>
        }
      />

      <Tabs defaultValue="overview">
        {s.computing ? (
          <p className="mb-2 text-xs text-amber-700" data-testid="metrics-computing">
            Counters are being calculated in the background for this large list — refreshing
            automatically.
          </p>
        ) : null}
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="classification" data-testid="tab-classification">Classification</TabsTrigger>
          <TabsTrigger value="errors" data-testid="tab-errors">Import errors</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6">
            <KpiTile label="Total contacts" value={(s.total ?? 0).toLocaleString()} testId="kpi-total" />
            <KpiTile label="Calling progress" value={pct(s.calling_progress)} testId="kpi-progress" />
            <KpiTile label="Answer rate" value={pct(s.answer_rate)} testId="kpi-answer" />
            <KpiTile label="Interested" value={(s.interested ?? 0).toLocaleString()} tone="success" testId="kpi-interested" />
            <KpiTile label="Interest rate" value={pct(s.interest_rate)} testId="kpi-interest-rate" />
            <KpiTile label="Callbacks" value={(s.callback_requested ?? 0).toLocaleString()} testId="kpi-callbacks" />
            <KpiTile label="Qualified leads" value={(s.qualified_leads ?? 0).toLocaleString()} tone="primary" testId="kpi-leads" />
            <KpiTile label="Converted" value={(s.converted ?? 0).toLocaleString()} tone="success" testId="kpi-converted" />
            <KpiTile label="DNC" value={(s.dnc ?? 0).toLocaleString()} tone="danger" testId="kpi-dnc" />
            <KpiTile label="Not called" value={(s.not_called ?? 0).toLocaleString()} testId="kpi-not-called" />
            <KpiTile label="Cost" value={money(s.cost)} testId="kpi-cost" />
            <KpiTile label="Cost / interested" value={s.cost_per_interested_lead != null ? money(s.cost_per_interested_lead) : "—"} testId="kpi-cost-per-lead" />
          </div>
          <Section title="Import summary" testId="list-import-summary">
            <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4 lg:grid-cols-7">
              {[["Total rows", data.stats?.total_rows], ["Valid", data.stats?.valid_contacts],
                ["Invalid", data.stats?.invalid_rows], ["Duplicates", data.stats?.duplicate_rows],
                ["Existing", data.stats?.existing_contacts], ["New", data.stats?.new_contacts],
                ["DNC matches", data.stats?.dnc_matches]].map(([label, value]) => (
                <div key={label} className="rounded-md border border-slate-200 px-3 py-2">
                  <p className="kpi-label">{label}</p>
                  <p className="text-sm font-medium text-slate-800">{(value ?? 0).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <Section title="Contacts (cursor paginated)" testId="list-contacts-section">
            <DataTable
              testId="list-contacts-table"
              rowTestId="list-contact-row"
              loading={contacts.isLoading}
              columns={[
                { key: "phone", label: "Phone", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
                { key: "first_name", label: "First name" },
                { key: "last_name", label: "Last name" },
                { key: "company", label: "Company", render: (r) => r.company || "—" },
                { key: "country", label: "Country" },
                { key: "language", label: "Lang", render: (r) => (r.language || "").toUpperCase() },
                { key: "contact_status", label: "Status", render: (r) => title(r.contact_status || "new") },
                { key: "call_outcome", label: "Outcome", render: (r) => title(r.call_outcome || "—") },
                { key: "interest_level", label: "Interest", render: (r) => title(r.interest_level || "unknown") },
                { key: "interest_score", label: "Score", render: (r) => r.interest_score ?? "—" },
                { key: "do_not_call", label: "DNC", render: (r) => (r.do_not_call ? "Yes" : "No") },
              ]}
              rows={contacts.data?.items || []}
              empty={{ title: "No contacts in this list", description: "Import a CSV to populate the list." }}
            />
            <div className="flex items-center justify-between px-4 py-3">
              <Button size="sm" variant="outline" disabled={!cursor} onClick={() => setCursor(null)} data-testid="contacts-first-page">First page</Button>
              <Button size="sm" variant="outline" disabled={!contacts.data?.next_cursor} onClick={() => setCursor(contacts.data.next_cursor)} data-testid="contacts-next-page">Next</Button>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="classification" className="mt-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <Distribution label="Contact status" rows={classification.data?.contact_status} testId="dist-contact-status" />
            <Distribution label="Call outcome" rows={classification.data?.call_outcome} testId="dist-call-outcome" />
            <Distribution label="Interest level" rows={classification.data?.interest_level} testId="dist-interest-level" />
            <Distribution label="Lead category" rows={classification.data?.lead_category} testId="dist-lead-category" />
            <Distribution label="Pipeline stage" rows={classification.data?.pipeline_stage} testId="dist-pipeline-stage" />
            <Distribution label="Product" rows={classification.data?.product} testId="dist-product" />
            <Distribution label="Campaign" rows={classification.data?.campaign} testId="dist-campaign" />
            <Distribution label="Country" rows={classification.data?.country} testId="dist-country" />
            <Distribution label="Language" rows={classification.data?.language} testId="dist-language" />
          </div>
        </TabsContent>

        <TabsContent value="errors" className="mt-4">
          <Section title={`${errors.data?.total ?? 0} invalid rows`} testId="list-errors-section">
            <DataTable
              testId="list-errors-table"
              rowTestId="list-error-row"
              loading={errors.isLoading}
              columns={[
                { key: "line", label: "Line" },
                { key: "phone", label: "Value" },
                { key: "reason", label: "Reason", render: (r) => title(r.reason) },
              ]}
              rows={errors.data?.items || []}
              empty={{ title: "No invalid rows", description: "Every row in this import was valid." }}
            />
          </Section>
        </TabsContent>
      </Tabs>

      <ImportWizard list={importOpen ? data : null} onClose={() => setImportOpen(false)} />
    </>
  );
}
